// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useMemo, type ReactNode } from 'react';

import { api } from '../../../api/client';
import type { SurveyPart, SurveyType } from '../../../api/types';
import { useCanEdit } from '../../../app/CaseRoleContext';
import type { CaseActions, CaseData } from '../../../app/bygning/useCaseData';
import { useArmedConfirm } from '../../../app/useArmedConfirm';
import { partCountText } from '../../../bygning/galleri';
import { lensToken } from '../../../bygning/lens';
import { geomTag, partMassText, partsCarryMass } from '../../../bygning/measure';
import { isScanPart } from '../../../bygning/provenance';
import { activeFilterCount, type FilteredResources } from '../../../bygning/resourceFilter';
import { reviewPill } from '../../../bygning/review';
import { planToast } from '../../../bygning/parts';
import { NO_FILTER_PATCH, togglePart, type Selection } from '../../../bygning/selection';
import { roomName } from '../../../kortlaegning/model';
import { partDesignation, resourceIndex } from '../../../kortlaegning/resources';
import { photoCountText } from '../../../kortlaegning/photo';
import { rowThumbView } from '../../../kortlaegning/view';
import { formatNumber } from '../../../kortlaegning/vocab';
import { Button } from '../../Button';
import { Checkbox } from '../../Checkbox';
import { DownloadIcon, Menu } from '../../Menu';
import { Pill } from '../../Pill';
import { TextLink } from '../../TextLink';
import toolbar from '../toolbar.module.css';
import { RowThumb } from './RowThumb';
import styles from './Tabel.module.css';

export interface PartsTableProps {
  data: CaseData;
  sel: Selection;
  setSel: (patch: Partial<Selection>) => void;
  actions: CaseActions;
  /** The filtered set (`filterResources`): its parts are the rows, its dismissed parts follow. */
  filtered: FilteredResources;
  /** The Typer/Dele toggle and the shared filters (TabelTile), first in the toolbar. */
  switcher?: ReactNode;
}

/**
 * Tabel › Dele (v8 "dele" table): one row per building part across types —
 * code, type, rum, mængde (with how the model measured it — målt areal,
 * målt volumen, antal — or indtastet), masse (the part's tonnes from its
 * type's build-up; only a server from ReUseX schema v37 sends them), status.
 * The checkboxes are the PartBar's picks
 * (`selParts`); a row click focuses the part in the inspector. A manual part
 * can be deleted here (armed confirm); scan parts go through "Markér som fejl".
 * The rows are the shared filtered set's parts (`filtered`), the same
 * Galleri › Dele shows as cards and the Model's Dele strip lists; with
 * Fejldetektion, parts marked as an error follow at the end, muted, with a
 * Gendan button (`PATCH {dismissed: false}`; only a server that tracks
 * `dismissed` returns them). The toolbar's ⋯ holds the CSV export, as Typer's does.
 */
export function PartsTable({ data, sel, setSel, actions, filtered, switcher }: PartsTableProps) {
  const canEdit = useCanEdit();
  const { busy } = data;
  const index = useMemo(() => resourceIndex(data.resources), [data.resources]);
  const confirm = useArmedConfirm<string>(busy, sel.typeId);

  const rows = filtered.parts;
  const dismissedRows = filtered.dismissed;

  function restore(code: string) {
    void actions.patchPart(code, { dismissed: false }).then((part) => {
      if (part) actions.notify(planToast('restore', 1));
    });
  }

  const picked = new Set(sel.selParts);
  const pickable = rows.filter((r) => r.type.review_status !== 'rejected');
  const allPicked = pickable.length > 0 && pickable.every((r) => picked.has(r.part.code));
  const photos = data.photos?.parts ?? null;
  const showMass = useMemo(() => partsCarryMass(data.types.flatMap((t) => t.parts)), [data.types]);
  const cols = showMass ? 9 : 8;

  function quantityCell(part: SurveyPart, type: SurveyType) {
    const g = geomTag(part, type.unit);
    return (
      <>
        {formatNumber(part.quantity)} {type.unit}
        {g && (
          <span className={g.entered ? `${styles.qtag} ${styles.qtagEntered}` : styles.qtag} title={g.title}>
            {g.label}
          </span>
        )}
      </>
    );
  }

  return (
    <>
      <div className={toolbar.tools}>
        <div className={toolbar.toolsLead}>
          {switcher}
          <span className={toolbar.count}>{partCountText(rows.length, dismissedRows.length)}</span>
        </div>
        {data.types.length > 0 && (
          <div className={toolbar.toolsEnd}>
            <Menu
              label="Flere handlinger"
              items={[{ id: 'export-csv', label: 'Eksportér CSV', icon: <DownloadIcon />, href: api.csvExportUrl(), download: true }]}
            />
          </div>
        )}
      </div>
      <div className={styles.wrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.cellCheck}>
                <Checkbox
                  aria-label="Vælg alle viste dele"
                  checked={allPicked}
                  onChange={() =>
                    setSel({
                      selParts: allPicked
                        ? sel.selParts.filter((c) => !pickable.some((r) => r.part.code === c))
                        : [...new Set([...sel.selParts, ...pickable.map((r) => r.part.code)])],
                    })
                  }
                />
              </th>
              <th>Del</th>
              <th>Type</th>
              <th>Rum</th>
              <th className={styles.num}>Mængde</th>
              {showMass && <th className={styles.num}>Masse</th>}
              <th>Fotos</th>
              <th>Status</th>
              <th>
                <span className={styles.srOnly}>Handlinger</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && dismissedRows.length === 0 ? (
              <tr>
                <td className={styles.empty} colSpan={cols}>
                  {data.types.length === 0 ? (
                    'Ingen dele endnu.'
                  ) : (
                    <>
                      Ingen dele matcher filtrene.{' '}
                      {activeFilterCount(sel) > 0 && <TextLink onClick={() => setSel(NO_FILTER_PATCH)}>Nulstil filtre</TextLink>}
                    </>
                  )}
                </td>
              </tr>
            ) : (
              rows.map(({ part, type }) => {
                const token = lensToken(type, sel.lens);
                const pill = reviewPill(type);
                const manual = !isScanPart(part);
                const armed = confirm.armed === part.code;
                const designation = partDesignation(index.get(part.code), data.resourceKeys);
                return (
                  <tr
                    key={part.code}
                    aria-selected={sel.partCode === part.code}
                    onClick={() => setSel({ typeId: type.id, partCode: part.code, selParts: sel.selParts })}
                  >
                    <td className={styles.cellCheck} onClick={(e) => e.stopPropagation()}>
                      {type.review_status !== 'rejected' && (
                        <Checkbox
                          aria-label={`Vælg del ${part.code}`}
                          checked={picked.has(part.code)}
                          onChange={() => setSel({ selParts: togglePart(sel.selParts, part.code) })}
                        />
                      )}
                    </td>
                    <td className={styles.mono}>
                      <span className={styles.name}>
                        <RowThumb view={rowThumbView([part], index)} />
                        <span>
                          {part.starred && (
                            <span className={styles.star} role="img" aria-label="Vigtig">
                              ★{' '}
                            </span>
                          )}
                          {part.code}
                          {designation && <span className={styles.muted}> · {designation}</span>}
                        </span>
                      </span>
                    </td>
                    <td>
                      <span className={styles.name}>
                        <i
                          className={token ? styles.swatch : `${styles.swatch} ${styles.ghost}`}
                          style={token ? { background: `var(${token})` } : undefined}
                          aria-hidden="true"
                        />
                        {type.name}
                      </span>
                    </td>
                    <td>{roomName(part) || <span className={styles.muted}>–</span>}</td>
                    <td className={styles.num}>{quantityCell(part, type)}</td>
                    {showMass && (
                      <td className={styles.num} title={part.mass_method ?? undefined}>
                        {partMassText(part) ?? <span className={styles.muted}>–</span>}
                      </td>
                    )}
                    <td className={styles.muted}>{photoCountText(photos?.[part.code])}</td>
                    <td>
                      <span className={styles.name}>
                        <Pill tone={pill.tone}>{pill.label}</Pill>
                        {manual && <Pill variant="outline">Manuel</Pill>}
                        {part.orphaned && (
                          <Pill tone="warn" title="Instansen findes ikke længere — gennemgå eller flyt delen">
                            forældet
                          </Pill>
                        )}
                        {part.note && (
                          <span className={styles.muted} role="img" aria-label={`Har note: ${part.note}`} title={part.note}>
                            ✎
                          </span>
                        )}
                      </span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {canEdit && manual && (
                        <Button
                          tone="danger"
                          armed={armed}
                          disabled={busy}
                          aria-label={armed ? `Bekræft: slet ${part.code}` : `Slet ${part.code}`}
                          onBlur={confirm.disarm}
                          onClick={(e) => {
                            if (!armed) {
                              confirm.arm(part.code, e.currentTarget);
                              return;
                            }
                            confirm.disarm();
                            void actions.deleteResource(part.code).then((ok) => {
                              if (ok) {
                                setSel({
                                  selParts: sel.selParts.filter((c) => c !== part.code),
                                  ...(sel.partCode === part.code ? { partCode: null } : {}),
                                });
                              }
                            });
                          }}
                        >
                          {armed ? 'Bekræft' : 'Slet'}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
            {dismissedRows.map(({ part, type }) => (
              <tr key={part.code} className={styles.muted} aria-label={`${part.code} — markeret som fejldetektion`}>
                <td className={styles.cellCheck} />
                <td className={styles.mono}>{part.code}</td>
                <td>{type.name}</td>
                <td>{roomName(part) || '–'}</td>
                <td className={styles.num}>
                  {formatNumber(part.quantity)} {type.unit}
                </td>
                {showMass && <td />}
                <td />
                <td>
                  <Pill variant="outline">Fejldetektion</Pill>
                </td>
                <td>
                  {canEdit && (
                    <Button disabled={busy} aria-label={`Gendan ${part.code}`} title="Delen tæller med igen" onClick={() => restore(part.code)}>
                      Gendan
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
